/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCancelSchedule } from '../../../hooks/useCancelSchedule';
import { formatPhone } from '../../../utils/formatters'; // Supondo a criação de um utilitário
import { Order, ScheduleDetails } from '../../../types/types';
import Icon from '../../ui/Icon';
import ConfirmationModal from '../ConfirmationModal';
import { pushToDataLayer } from '../../../types/gtm';

interface FinalProps {
  scheduleInfos: ScheduleDetails;
  isVideoconference: boolean;
  order: Order | null;
  choiceAc: boolean;
  onSchedule?: (order: Order | null) => void;
  onCancelSchedule?: (id: string) => void;
  productInfos: {
    productName: string;
    cpf: string;
    cnpj: string;
    email: string;
  };
}

const InfoItem = ({
  iconName,
  title,
  children,
}: {
  iconName: string;
  title?: string;
  children: React.ReactNode;
}) => (
  <div className="flex items-start gap-2">
    <div className="flex items-baseline justify-center max-w-8 h-full pt-1">
      <Icon
        name={iconName}
        width={24}
        height={24}
        {...(iconName === 'User'
          ? { stroke: 'var(--ins-blue)', fill: 'transparent' }
          : { fill: 'var(--ins-blue)' })}
      />
    </div>
    <div className="flex flex-col">
      {title && <h3 className="font-semibold text-gray-900">{title}</h3>}
      {children}
    </div>
  </div>
);

const InstructionsList = ({ instructions }: { instructions: (string | JSX.Element)[] }) => (
  <ul className="flex flex-col list-none p-0 m-0 gap-6">
    {instructions.map((instruction, index) => {
      const isTitle = typeof instruction !== 'string' && instruction.type === 'h4';

      return (
        <li key={index} className={`flex items-start gap-2 ${isTitle ? '-mb-2' : ''}`}>
          {!isTitle && (
            <span className="flex items-baseline justify-center max-w-4 w-full pt-1">
              <Icon name="Checked" width={16} height={16} fill="var(--ins-blue)" />
            </span>
          )}
          <div className="text-sm font-normal text-gray-900">{instruction}</div>
        </li>
      );
    })}
  </ul>
);

const docLink = (
  <a
    href="https://suporte.certisign.com.br/certificado-digital/documentos-necessarios/"
    target="_blank"
    rel="noreferrer"
    className="text-ins-blue font-normal"
  >
    documentação necessária
  </a>
);

const presentialInstructions = [
  <>Verifique a {docLink}.</>,
  'Chegue com 15 minuto de antecedência.',
  'A validação deve ser feita pelo próprio titular ou por um representante legal. Não é possível realizar a validação por terceiros, exceto em casos onde tem uma procuração ou autorização judicial para relativamente incapaz.',
  'Para certificados A3 (somente certificado): se você já tem a mídia, será necessário levá-la para a validação.',
];

const Final = ({
  scheduleInfos,
  productInfos,
  order,
  onSchedule,
  onCancelSchedule,
  choiceAc,
  isVideoconference,
}: FinalProps) => {
  const { handleCancelClick } = useCancelSchedule({ onSchedule, onCancelSchedule });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();

  const videoconferenceInstructions = [
    <>Verifique e separe a {docLink}.</>,
    'Você vai receber um link por e-mail, WhatsApp ou SMS até 5 minutos antes do horário agendado.',
    <h4 className="text-sm font-semibold">Para a videoconferência</h4>,
    'Antes de iniciar, habilite o acesso a câmera e microfone do seu dispositivo. Se estiver usando a câmera em outro aplicativo, finalize o uso.',
    'Deixe seu rosto visível e evite usar óculos e boné.',
    'Esteja em local iluminado, com fundo neutro e sem pessoas atrás.',
  ];

  if (!scheduleInfos || !order) {
    return null;
  }

  const isPresential = !isVideoconference || choiceAc;
  const date = isPresential ? scheduleInfos.data : scheduleInfos.dataAgendamento;
  const time = isPresential ? scheduleInfos.hora : scheduleInfos.horaInicialAgendamento;
  const locationName = isPresential
    ? scheduleInfos.detalhePosto.nomeFantasia
    : scheduleInfos.nomePosto;
  const phone = isPresential ? scheduleInfos.detalhePosto.telefone : '';
  const phoneFormatted = formatPhone(phone);

  const locationAddress = isPresential
    ? `${scheduleInfos.detalhePosto.endereco}, ${scheduleInfos.detalhePosto.numero} - ${scheduleInfos.detalhePosto.bairro}, ${scheduleInfos.detalhePosto.cidade} - ${scheduleInfos.detalhePosto.uf}, ${scheduleInfos.detalhePosto.cep}`
    : 'Validação por Videoconferência';

  const handleReschedule = async () => {
    await handleCancelClick(scheduleInfos.pedido.toString(), 'reschedule', [order]);
    pushToDataLayer({
      event: 'select_content',
      content_type: 'validacao-resumo:pop-up-reagendar-meu-horario',
    });
    if (order?.id) {
      navigate('/selecionar');
    }
  };

  const handleOpenModalReschedule = () => {
    pushToDataLayer({
      event: 'select_content',
      content_type: 'validacao-resumo:reagendar',
    });
  };

  return (
    <>
      <div className="flex flex-col items-center md:items-start w-full">
        <div className="flex flex-col gap-3 mb-10 pt-5 px-5 md:px-0 md:pt-0">
          <h3 className="text-2xl text-ins-blue font-normal">
            Agendamento da validação confirmado.
          </h3>
          <p className="text-gray-900">Abaixo está o resumo do seu agendamento</p>
        </div>
        <div className="w-full flex md:flex-row flex-col items-center md:items-start justify-center gap-4">
          <div className="flex flex-col max-w-[608px] w-full h-full bg-white rounded-lg p-4 gap-6">
            <InfoItem iconName="User" title="Titular">
              <p className="font-normal text-gray-900 text-sm">
                {order.historicoPessoaFisica.nomeCliente || order.email}
              </p>
              <p className="font-normal text-gray-900 text-sm">CPF: {order.cpf}</p>
            </InfoItem>

            <InfoItem iconName="CalendarChecked">
              <h3 className="font-semibold text-gray-900">
                {new Date(`${date}T00:00:00`).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}
              </h3>
            </InfoItem>

            <InfoItem iconName="Clock">
              <h3 className="font-semibold text-gray-900">{time}</h3>
            </InfoItem>

            <InfoItem iconName="Location">
              <h3 className="font-semibold text-gray-900">{locationName}</h3>
              <p className="text-sm">{locationAddress}</p>
              {phoneFormatted && <p className="text-sm">{phoneFormatted}</p>}
            </InfoItem>

            <InfoItem iconName="Order" title="Pedido">
              <p className="text-sm">Número: {scheduleInfos.pedido}</p>
              <p className="text-sm">Produto: {productInfos.productName}</p>
            </InfoItem>

            <div className="flex items-center justify-between w-full">
              {/* <a
                onClick={() => pushToDataLayer({
                  event: 'select_content',
                  content_type: 'validacao-resumo:acompanhe-seu-agendamento'
                })}
                href="https://pagamento.certisign.com.br/default/customer/account/login/"
                target="_blank" rel="noreferrer"
                className='flex items-center justify-center no-underline text-ins-blue text-sm font-semibold py-2.5 px-4 h-10'
              >
                Acompanhe seu agendamento
              </a>*/}
              <button
                onClick={() => {
                  setIsModalOpen(true);
                  handleOpenModalReschedule();
                }} // Apenas abre o modal
                className="flex items-center justify-center bg-transparent border-none gap-2 text-ins-blue text-sm font-semibold py-2.5 px-4 h-10"
              >
                <Icon name="Calendar" width={16} height={16} fill="var(--ins-blue)" />
                Reagendar
              </button>
            </div>
          </div>
          <div className="flex flex-col max-w-[608px] w-full h-full bg-white rounded-lg p-4 gap-6">
            <div className="flex items-center mb-10 gap-2">
              <Icon name="Informative" width={36} height={36} fill="var(--ins-blue)" />
              <h3 className="text-xl text-ins-blue">Importante</h3>
            </div>
            <InstructionsList
              instructions={
                isVideoconference ? videoconferenceInstructions : presentialInstructions
              }
            />
          </div>
        </div>
      </div>
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleReschedule}
        title="Reagendar validação"
      >
        <p>
          Ao continuar, <b>o dia e horário já confirmados serão cancelados.</b> Será necessário
          escolher uma nova data e confirmar o novo horário.
        </p>
      </ConfirmationModal>
    </>
  );
};

export default Final;
